import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft, Buildings, Phone, EnvelopeSimple, Globe, MapPin,
  Fire, Snowflake, SunDim, CircleNotch, PaperPlaneTilt,
  ChatCircleDots, Robot, PhoneCall, Notepad, CalendarCheck,
  ArrowsClockwise, CaretDown, UserPlus, Trash, PencilSimple,
  Lightning, Copy, Check, Warning, FileText, User
} from '@phosphor-icons/react';


const STATUS_CONFIG = {
  new: { label: 'Nuevo', color: 'bg-slate-500', ring: 'ring-slate-200' },
  contacted: { label: 'Contactado', color: 'bg-blue-500', ring: 'ring-blue-200' },
  interested: { label: 'Interesado', color: 'bg-amber-500', ring: 'ring-amber-200' },
  qualified: { label: 'Calificado', color: 'bg-violet-500', ring: 'ring-violet-200' },
  converted: { label: 'Convertido', color: 'bg-green-500', ring: 'ring-green-200' },
  lost: { label: 'Perdido', color: 'bg-red-500', ring: 'ring-red-200' },
};

const STATUSES = ['new', 'contacted', 'interested', 'qualified', 'converted', 'lost'];

const TEMP_ICONS = {
  hot: { icon: Fire, color: 'text-red-500', bg: 'bg-red-50', label: 'Hot' },
  warm: { icon: SunDim, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Warm' },
  cold: { icon: Snowflake, color: 'text-blue-400', bg: 'bg-blue-50', label: 'Cold' },
};

const ACTIVITY_ICONS = {
  call: PhoneCall,
  email: EnvelopeSimple,
  note: Notepad,
  meeting: CalendarCheck,
  status_change: ArrowsClockwise,
  converted: UserPlus,
};

export default function ProspectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  // Email state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Activity state
  const [actType, setActType] = useState('call');
  const [actDesc, setActDesc] = useState('');
  const [actFollowup, setActFollowup] = useState('');
  const [savingAct, setSavingAct] = useState(false);

  // AI state
  const [aiScenario, setAiScenario] = useState('first_contact');
  const [aiResult, setAiResult] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Sales team state
  const [salesTeam, setSalesTeam] = useState([]);

  const fetchProspect = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/prospects/${id}`, { withCredentials: true });
      setProspect(res.data);
      setEditForm(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchProspect(); }, [fetchProspect]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // Fetch templates and sales team
  useEffect(() => {
    const fetchExtra = async () => {
      try {
        const [tRes, sRes] = await Promise.all([
          axios.get(`${API_URL}/api/b2b/templates`, { withCredentials: true }),
          axios.get(`${API_URL}/api/b2b/sales-team`, { withCredentials: true })
        ]);
        setTemplates(tRes.data);
        setSalesTeam(sRes.data);
      } catch (err) { console.error(err); }
    };
    fetchExtra();
  }, []);

  const updateStatus = async (newStatus) => {
    try {
      await axios.post(`${API_URL}/api/b2b/prospects/${id}/update-status`, { status: newStatus }, { withCredentials: true });
      fetchProspect();
    } catch (err) {
      alert('Error al actualizar estado');
    }
  };

  const handleConvert = async () => {
    if (!window.confirm('Convertir este prospecto en Lead del CRM?')) return;
    try {
      await axios.post(`${API_URL}/api/b2b/prospects/${id}/convert`, {}, { withCredentials: true });
      fetchProspect();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al convertir');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminar este prospecto permanentemente?')) return;
    try {
      await axios.delete(`${API_URL}/api/b2b/prospects/${id}`, { withCredentials: true });
      navigate('/prospects');
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailSubject || !emailBody) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API_URL}/api/b2b/prospects/${id}/send-email`, {
        subject: emailSubject,
        html: `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.6">${emailBody.replace(/\n/g, '<br>')}</div>`
      }, { withCredentials: true });
      setEmailSubject('');
      setEmailBody('');
      fetchProspect();
      alert('Email enviado correctamente');
    } catch (err) {
      alert('Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    if (!actDesc) return;
    setSavingAct(true);
    try {
      await axios.post(`${API_URL}/api/b2b/prospects/${id}/log-activity`, {
        type: actType,
        description: actDesc,
        next_followup: actFollowup || null
      }, { withCredentials: true });
      setActDesc('');
      setActFollowup('');
      fetchProspect();
    } catch (err) {
      alert('Error al registrar actividad');
    } finally {
      setSavingAct(false);
    }
  };

  const handleGenerateScript = async () => {
    setAiLoading(true);
    setAiResult('');
    try {
      const res = await axios.post(`${API_URL}/api/b2b/ai/generate-script`, {
        prospect_id: id,
        scenario: aiScenario
      }, { withCredentials: true });
      setAiResult(res.data.script || '');
    } catch (err) {
      setAiResult('Error al generar script. Intenta de nuevo.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const userMsg = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/ai/chat`, {
        messages: [...chatMessages, userMsg]
      }, { withCredentials: true });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.message }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error de conexion con Alex. Intenta de nuevo.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const { id: _, created_at, created_by, activities, ...payload } = editForm;
      await axios.put(`${API_URL}/api/b2b/prospects/${id}`, payload, { withCredentials: true });
      setEditing(false);
      fetchProspect();
    } catch (err) {
      alert('Error al guardar');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApplyTemplate = async (templateId) => {
    try {
      const res = await axios.post(`${API_URL}/api/b2b/templates/${templateId}/render`, {
        prospect_id: id
      }, { withCredentials: true });
      setEmailSubject(res.data.subject);
      setEmailBody(res.data.body);
      setActiveTab('email');
    } catch (err) {
      alert('Error al aplicar template');
    }
  };

  const handleAssign = async (assignedTo) => {
    try {
      await axios.post(`${API_URL}/api/b2b/prospects/${id}/assign`, {
        assigned_to: assignedTo || null
      }, { withCredentials: true });
      fetchProspect();
    } catch (err) {
      alert('Error al asignar');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>;
  if (!prospect) return <div className="text-center py-20 text-slate-500">Prospecto no encontrado</div>;

  const temp = TEMP_ICONS[prospect.temperature] || TEMP_ICONS.cold;
  const TempIcon = temp.icon;
  const stConfig = STATUS_CONFIG[prospect.status] || STATUS_CONFIG.new;
  const currentIdx = STATUSES.indexOf(prospect.status);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="prospect-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/prospects')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="back-to-prospects">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900">{prospect.company_name}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${stConfig.color}`}>{stConfig.label}</span>
            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${temp.bg} ${temp.color} flex items-center gap-1`}>
              <TempIcon size={14} weight="fill" /> {temp.label}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{prospect.industry || 'Sin industria'} {prospect.contact_name && `| ${prospect.contact_name}`}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* Assign to sales rep */}
          <select
            value={prospect.assigned_to || ''}
            onChange={(e) => handleAssign(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="assign-select"
          >
            <option value="">Sin asignar</option>
            {salesTeam.map(m => (
              <option key={m.name} value={m.name}>{m.name} ({m.prospect_count})</option>
            ))}
          </select>
          {prospect.status !== 'converted' && prospect.status !== 'lost' && (
            <button onClick={handleConvert} className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium" data-testid="convert-to-lead-btn">
              <UserPlus size={16} weight="bold" /> Convertir a Lead
            </button>
          )}
          <button onClick={handleDelete} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid="delete-prospect-btn">
            <Trash size={18} />
          </button>
        </div>
      </div>

      {/* Status Pipeline */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="status-pipeline">
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {STATUSES.map((s, i) => {
            const sc = STATUS_CONFIG[s];
            const isActive = prospect.status === s;
            const isPast = i < currentIdx;
            return (
              <button
                key={s}
                onClick={() => updateStatus(s)}
                disabled={prospect.status === 'converted'}
                className={`flex-1 min-w-[90px] py-2 px-2 rounded-lg text-xs font-semibold text-center transition-all ${
                  isActive ? `${sc.color} text-white shadow-md` :
                  isPast ? 'bg-slate-100 text-slate-600' :
                  'bg-slate-50 text-slate-400 hover:bg-slate-100'
                }`}
                data-testid={`status-btn-${s}`}
              >
                {sc.label}
              </button>
            );
          })}
        </div>
        {prospect.score > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-slate-500">Score:</span>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-500" style={{ width: `${prospect.score}%` }} />
            </div>
            <span className="text-xs font-bold text-slate-700">{prospect.score}</span>
          </div>
        )}
      </div>

      {prospect.converted_lead_id && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-testid="converted-banner">
          <Check size={20} className="text-green-600" weight="bold" />
          <p className="text-sm text-green-800 font-medium">Este prospecto fue convertido a Lead del CRM</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1" data-testid="detail-tabs">
        {[
          { key: 'info', label: 'Info', icon: Buildings },
          { key: 'activities', label: 'Actividades', icon: Notepad },
          { key: 'email', label: 'Email', icon: EnvelopeSimple },
          { key: 'templates', label: 'Templates', icon: FileText },
          { key: 'ai', label: 'Alex AI', icon: Robot },
          { key: 'chat', label: 'Chat AI', icon: ChatCircleDots },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-lg text-xs font-semibold transition-all ${
                activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <Icon size={16} /> <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* INFO TAB */}
        {activeTab === 'info' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="info-tab">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Informacion del Prospecto</h3>
              <button onClick={() => { setEditing(!editing); setEditForm(prospect); }} className="text-sm text-blue-600 hover:underline flex items-center gap-1" data-testid="edit-toggle-btn">
                <PencilSimple size={14} /> {editing ? 'Cancelar' : 'Editar'}
              </button>
            </div>
            {editing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'company_name', label: 'Empresa', required: true },
                    { key: 'industry', label: 'Industria' },
                    { key: 'contact_name', label: 'Contacto' },
                    { key: 'contact_title', label: 'Cargo' },
                    { key: 'contact_email', label: 'Email', type: 'email' },
                    { key: 'contact_phone', label: 'Telefono' },
                    { key: 'website', label: 'Website' },
                    { key: 'city', label: 'Ciudad' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-xs font-medium text-slate-600">{f.label}</label>
                      <input
                        type={f.type || 'text'}
                        value={editForm[f.key] || ''}
                        onChange={e => setEditForm({ ...editForm, [f.key]: e.target.value })}
                        required={f.required}
                        className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Notas</label>
                  <textarea
                    value={editForm.notes || ''}
                    onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={3}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium" data-testid="save-edit-btn">
                  Guardar Cambios
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                {[
                  { icon: Buildings, label: 'Empresa', value: prospect.company_name },
                  { icon: Buildings, label: 'Industria', value: prospect.industry || '-' },
                  { icon: null, label: 'Contacto', value: prospect.contact_name ? `${prospect.contact_name}${prospect.contact_title ? ` (${prospect.contact_title})` : ''}` : '-' },
                  { icon: Phone, label: 'Telefono', value: prospect.contact_phone || '-' },
                  { icon: EnvelopeSimple, label: 'Email', value: prospect.contact_email || '-' },
                  { icon: Globe, label: 'Website', value: prospect.website || '-' },
                  { icon: MapPin, label: 'Ubicacion', value: [prospect.city, prospect.state].filter(Boolean).join(', ') || '-' },
                  { icon: null, label: 'Empleados', value: prospect.employee_count || '-' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                      {f.icon ? <f.icon size={16} className="text-slate-400" /> : <Buildings size={16} className="text-slate-400" />}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{f.label}</p>
                      <p className="text-sm font-medium text-slate-900">{f.value}</p>
                    </div>
                  </div>
                ))}
                {prospect.services_needed?.length > 0 && (
                  <div className="md:col-span-2">
                    <p className="text-xs text-slate-500 mb-1">Servicios Necesarios</p>
                    <div className="flex flex-wrap gap-1.5">
                      {prospect.services_needed.map(s => (
                        <span key={s} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {prospect.notes && (
                  <div className="md:col-span-2 mt-2">
                    <p className="text-xs text-slate-500 mb-1">Notas</p>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{prospect.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ACTIVITIES TAB */}
        {activeTab === 'activities' && (
          <div className="space-y-4" data-testid="activities-tab">
            {/* Log Activity Form */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Registrar Actividad</h3>
              <form onSubmit={handleLogActivity} className="space-y-3">
                <div className="flex gap-2 flex-wrap">
                  {['call', 'email', 'note', 'meeting'].map(t => {
                    const Icon = ACTIVITY_ICONS[t];
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setActType(t)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          actType === t ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`act-type-${t}`}
                      >
                        <Icon size={14} /> {t === 'call' ? 'Llamada' : t === 'email' ? 'Email' : t === 'note' ? 'Nota' : 'Reunion'}
                      </button>
                    );
                  })}
                </div>
                <textarea
                  value={actDesc}
                  onChange={e => setActDesc(e.target.value)}
                  placeholder="Descripcion de la actividad..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  required
                  data-testid="activity-description"
                />
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-slate-500">Proximo seguimiento (opcional)</label>
                    <input
                      type="datetime-local"
                      value={actFollowup}
                      onChange={e => setActFollowup(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      data-testid="activity-followup"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={savingAct}
                    className="mt-5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                    data-testid="log-activity-btn"
                  >
                    {savingAct ? <CircleNotch size={16} className="animate-spin" /> : 'Registrar'}
                  </button>
                </div>
              </form>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Historial de Actividades</h3>
              {(!prospect.activities || prospect.activities.length === 0) ? (
                <p className="text-sm text-slate-400 text-center py-8">Sin actividades registradas</p>
              ) : (
                <div className="space-y-0">
                  {[...prospect.activities].reverse().map((a, i) => {
                    const Icon = ACTIVITY_ICONS[a.type] || Notepad;
                    return (
                      <div key={i} className="flex gap-3 pb-4 relative" data-testid={`activity-item-${i}`}>
                        {i < prospect.activities.length - 1 && (
                          <div className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200" />
                        )}
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0 z-10">
                          <Icon size={14} className="text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900">{a.description}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{a.user} - {new Date(a.date).toLocaleString('es-US')}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* EMAIL TAB */}
        {activeTab === 'email' && (
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="email-tab">
            <h3 className="font-semibold text-slate-900 mb-1">Enviar Email</h3>
            <p className="text-sm text-slate-500 mb-4">Enviar a: {prospect.contact_email || 'Sin email configurado'}</p>
            {!prospect.contact_email ? (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg text-sm">
                <Warning size={18} /> Agrega un email de contacto primero
              </div>
            ) : (
              <form onSubmit={handleSendEmail} className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Asunto</label>
                  <input
                    value={emailSubject}
                    onChange={e => setEmailSubject(e.target.value)}
                    placeholder="Ej: Servicios de baja tension para su empresa"
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    required
                    data-testid="email-subject"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Mensaje</label>
                  <textarea
                    value={emailBody}
                    onChange={e => setEmailBody(e.target.value)}
                    placeholder="Escriba el cuerpo del email..."
                    rows={8}
                    className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    required
                    data-testid="email-body"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={sendingEmail}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                    data-testid="send-email-btn"
                  >
                    {sendingEmail ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
                    {sendingEmail ? 'Enviando...' : 'Enviar Email'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAiScenario('email'); setActiveTab('ai'); }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 text-sm font-medium"
                    data-testid="generate-email-ai-btn"
                  >
                    <Robot size={16} /> Generar con Alex AI
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* TEMPLATES TAB */}
        {activeTab === 'templates' && (
          <div className="space-y-4" data-testid="templates-tab">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-900 mb-1">Plantillas de Email</h3>
              <p className="text-sm text-slate-500 mb-4">Selecciona una plantilla y se rellenara con los datos de {prospect.company_name}</p>
              {templates.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No hay plantillas. Se crean desde el panel de administracion.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map(t => (
                    <div key={t.id} className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-testid={`template-${t.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          t.category === 'intro' ? 'bg-blue-100 text-blue-700' :
                          t.category === 'follow_up' ? 'bg-amber-100 text-amber-700' :
                          t.category === 'proposal' ? 'bg-violet-100 text-violet-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {t.category === 'intro' ? 'Introduccion' :
                           t.category === 'follow_up' ? 'Seguimiento' :
                           t.category === 'proposal' ? 'Propuesta' :
                           'Agradecimiento'}
                        </span>
                      </div>
                      <h4 className="font-medium text-sm text-slate-900 mb-1">{t.name}</h4>
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{t.subject}</p>
                      <button
                        onClick={() => handleApplyTemplate(t.id)}
                        className="w-full px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
                        data-testid={`apply-template-${t.id}`}
                      >
                        Usar esta plantilla
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI SCRIPTS TAB */}
        {activeTab === 'ai' && (
          <div className="space-y-4" data-testid="ai-tab">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-1">
                <Robot size={20} className="text-violet-600" />
                <h3 className="font-semibold text-slate-900">Alex AI - Generador de Scripts</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Genera scripts personalizados para {prospect.company_name}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  { key: 'first_contact', label: 'Primera Llamada', icon: PhoneCall },
                  { key: 'follow_up', label: 'Seguimiento', icon: ArrowsClockwise },
                  { key: 'objection', label: 'Objeciones', icon: Warning },
                  { key: 'email', label: 'Email Intro', icon: EnvelopeSimple },
                ].map(s => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setAiScenario(s.key)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        aiScenario === s.key ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`ai-scenario-${s.key}`}
                    >
                      <Icon size={14} /> {s.label}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={handleGenerateScript}
                disabled={aiLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium disabled:opacity-50"
                data-testid="generate-script-btn"
              >
                {aiLoading ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
                {aiLoading ? 'Generando...' : 'Generar Script'}
              </button>
            </div>

            {aiResult && (
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ai-result">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm text-slate-900">Resultado</h4>
                  <button
                    onClick={() => copyToClipboard(aiResult)}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600"
                    data-testid="copy-script-btn"
                  >
                    {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                </div>
                <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-4 max-h-[500px] overflow-y-auto text-sm leading-relaxed">
                  {aiResult}
                </div>
              </div>
            )}
          </div>
        )}

        {/* CHAT TAB */}
        {activeTab === 'chat' && (
          <div className="bg-white rounded-xl border border-slate-200 flex flex-col h-[500px]" data-testid="chat-tab">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                <Robot size={18} className="text-violet-600" />
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-900">Alex</p>
                <p className="text-xs text-slate-500">Asistente de Ventas B2B</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Robot size={40} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Preguntale a Alex sobre estrategias de venta, manejo de objeciones, o como abordar a {prospect.company_name}.</p>
                </div>
              )}
              {chatMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-2xl px-4 py-3 rounded-bl-md">
                    <CircleNotch size={18} className="animate-spin text-violet-500" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <form onSubmit={handleChat} className="p-3 border-t border-slate-200 flex gap-2">
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
                disabled={chatLoading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={chatLoading || !chatInput.trim()}
                className="px-4 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:opacity-50 transition-colors"
                data-testid="chat-send-btn"
              >
                <PaperPlaneTilt size={18} />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
